<template>
  <div ref="appTable" class="app-table">
    <div class="app-table-header-wrapper">
      <table class="table is-fullwidth">
        <colgroup>
          <col
            v-for="header in parsedHeaders"
            :key="header.value"
            :name="header.value"
            :style="{
              width:
                typeof header.width === 'number'
                  ? header.width + 'px'
                  : header.width
            }"
          />
        </colgroup>
        <thead>
          <tr>
            <th
              v-for="header in parsedHeaders"
              :key="header.value"
              :class="header.class"
              :align="header.align"
            >
              <div class="cell">
                <slot :name="'header.' + header.value" :header="header">
                  {{ header.text }}
                </slot>
                <button
                  v-if="header.sortable"
                  class="sort-button"
                  @click="toggleSort(header.value)"
                >
                  <span class="icon">
                    <font-awesome-icon
                      :icon="
                        sortInternal[header.value] == 1
                          ? 'sort-up'
                          : sortInternal[header.value] == -1
                          ? 'sort-down'
                          : 'sort'
                      "
                    ></font-awesome-icon>
                  </span>
                </button>
              </div>
            </th>
          </tr>
        </thead>
      </table>
    </div>

    <table class="table is-fullwidth">
      <colgroup>
        <col
          v-for="header in parsedHeaders"
          :key="header.value"
          :name="header.value"
          :style="{
            width:
              typeof header.width === 'number'
                ? header.width + 'px'
                : header.width
          }"
        />
      </colgroup>
      <tbody>
        <slot
          v-for="(item, idx) in items"
          name="item"
          :item="item"
          :headers="parsedHeaders"
        >
          <tr
            :key="idx"
            :class="{
              'is-clickable': hasClickRowListener
            }"
            @click="clickRow(item)"
          >
            <td
              v-for="header in parsedHeaders"
              :key="'item.' + header.value"
              :class="header.class"
              :align="header.align"
            >
              <div class="cell" :class="header.cellClass">
                <slot
                  :name="'item.' + header.value"
                  :item="item"
                  :header="header"
                >
                  {{ getProperty(item, header.value, header.parser) }}
                </slot>
              </div>
            </td>
          </tr>
        </slot>
      </tbody>
    </table>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'AppTable',
  props: {
    headers: {
      required: true,
      type: Array
    },
    items: {
      required: true,
      type: Array
    },
    clickRowAccess: {
      type: Boolean,
      default: true
    },
    sort: {
      type: Object
    }
  },
  data() {
    return {
      sortInternal: {}
    };
  },
  computed: {
    parsedHeaders() {
      return this.headers.filter(header =>
        header.permissions ? this.hasPermission(header.permissions) : true
      );
    },
    hasClickRowListener() {
      return (
        this.$listeners && !!this.$listeners['click:row'] && this.clickRowAccess
      );
    }
  },
  watch: {
    sort: {
      deep: true,
      // immediate: true,
      handler() {
        if (!_.isEqual(this.sort, this.sortInternal)) {
          this.sortInternal = Object.assign({}, this.sort);
        }
        // for (const key in this.sort) {
        //   if (this.sort.hasOwnProperty(key)) {
        //     this.$set(this.sortInternal, key, this.sort[key]);
        //   }
        // }
      }
    },
    sortInternal: {
      deep: true,
      handler(value) {
        this.$emit('update:sort', value);
      }
    }
  },
  methods: {
    getProperty(item, key, parser) {
      const value = _.get(item, key);
      if (typeof parser == 'function') {
        return parser(value);
      }
      return value;
    },
    clickRow(item) {
      if (this.clickRowAccess) {
        this.$emit('click:row', item);
      }
    },
    toggleSort(key) {
      for (const k in this.sortInternal) {
        if (k != key) {
          this.$delete(this.sortInternal, k);
        }
      }
      switch (this.sortInternal[key]) {
        case 1:
          this.$set(this.sortInternal, key, -1);
          break;
        case -1:
          this.$delete(this.sortInternal, key);
          // this.$set(this.sortInternal, key, 0);
          break;
        default:
        case 0:
          this.$set(this.sortInternal, key, 1);
          break;
      }
    }
  }
};
</script>

<style lang="scss">
.app-table {
  &:not(:last-child) {
    margin-bottom: 2rem;
  }
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 200px;
  .table {
    table-layout: fixed;
  }

  .cell {
    padding-left: $table-cell-horizontal-padding;
    padding-right: $table-cell-horizontal-padding;

    .button {
      margin: 0 10px;
    }
  }
}
.scrollbar {
  .ps__rail-y {
    cursor: pointer;
    display: block !important;
    opacity: 0.2;

    &:hover,
    &:focus,
    &:active {
      background: transparent !important;
    }
  }
}

.scrollable-container {
  overflow-y: auto;
  position: relative;
  flex: 1;
  .table {
    position: absolute;
    top: 0px;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
  }
}
.table {
  margin-bottom: 0 !important;
  thead {
    tr {
      th {
        border: none;
        font-family: $family-secondary;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        font-size: 1.4rem;
        &[align='center'] {
          .cell {
            justify-content: center;
            text-align: center;
          }
        }
        &[align='end'] {
          .cell {
            justify-content: flex-end;
            text-align: right;
          }
        }
      }
    }
    .cell {
      display: flex;
      align-items: center;
    }
  }

  tbody {
    tr {
      transition: all 300ms ease;
      &:hover {
        opacity: 0.8;
      }
      &:first-child {
        td {
          &:first-child {
            border-top-left-radius: $radius-large;
          }
          &:last-child {
            border-top-right-radius: $radius-large;
          }
        }
      }
      &:last-child {
        td {
          &:first-child {
            border-bottom-left-radius: $radius-large;
          }
          &:last-child {
            border-bottom-right-radius: $radius-large;
          }
        }
      }
      td {
        vertical-align: middle;
        background-color: $overlay-white;
        font-weight: $weight-medium;

        &:last-child {
          .cell {
            overflow: none;
            text-overflow: unset;
          }
        }

        .cell {
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 1.6rem;
        }
      }
    }
  }
}

.sort-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
}
</style>
